/**
 * What each status means, per kind of record (P47, Pedro 2026-10-16: "add a tooltip to all
 * labels such as these (Prospect, Active, Inactive, Deleted), in all tables, explaining what
 * it means in that context"). One source, so the filter pills and the row badges of the same
 * list explain a status in the same words (§3h / §3j).
 *
 * Meanings follow Help (Day-to-day §3) and the lifecycle code (services/cascade_lifecycle:
 * an agreement going Inactive takes its policies and accounts with it; models/account: an
 * account with no agreement and no policy is Inactive; a terminated policy and its accounts
 * are Closed).
 */
export type StatusHelpEntity =
  | 'agreement' | 'account' | 'policy' | 'person' | 'party' | 'task' | 'change_request' | 'user'
  | 'interaction' | 'security' | 'trade';

export const STATUS_HELP: Record<StatusHelpEntity, Record<string, string>> = {
  agreement: {
    Prospect: 'A relationship being set up. It has no client number yet (CIIF-PRO), but can already hold accounts and policies.',
    Active: 'A signed client, with its permanent client number.',
    Inactive: 'Suspended, a former client, or a lost lead, always with a reason. It can be made Active again.',
    Deleted: 'Removed by mistake or for clean-up. Admin or Compliance can restore it.',
  },
  account: {
    Active: 'In use, held under an agreement directly or through a policy.',
    Inactive: 'Not in use: its agreement or policy is Inactive, or it is attached to neither.',
    Closed: 'Closed for good, for example because its policy was terminated.',
    Deleted: 'Removed by mistake or for clean-up. Admin or Compliance can restore it.',
  },
  policy: {
    Active: 'In force, linked to its agreement.',
    Inactive: 'Not in force for now, usually because its agreement is Inactive. It can be made Active again.',
    Closed: 'Terminated (redeemed, lapsed or matured). Its accounts are closed too.',
    Deleted: 'Removed by mistake or for clean-up. Admin or Compliance can restore it.',
  },
  person: {
    Active: 'Holds a role on an Active or Prospect agreement, or on an Active account or policy.',
    Inactive: 'Holds no role on any active record. Still a valid contact: can be edited, linked and emailed.',
    Deleted: 'Removed by mistake or for clean-up. Admin or Compliance can restore it.',
  },
  party: {
    Active: 'Current: this person holds this role now.',
    Deleted: 'Removed: this person no longer holds this role. Kept for the record.',
  },
  task: {
    OPEN: 'Still to do. Shown in red once its deadline has passed.',
    DONE: 'Completed.',
    CANCELLED: 'No longer needed. Kept for history and can be reopened.',
    DELETED: 'Removed. Admin or Compliance can restore it.',
  },
  change_request: {
    PENDING: 'Waiting for Compliance or an Admin to approve or reject it.',
    APPLIED: 'Approved, and the change is now on the record.',
    REJECTED: 'Not approved. The record was left unchanged.',
    CANCELLED: 'Withdrawn before a decision. The record was left unchanged.',
    ALL: 'Every change request, whatever its status.',
  },
  interaction: {
    Deleted: 'Removed. Kept in the audit trail; Admin or Compliance can restore it.',
  },
  // TRADE-FLOW (Pedro 2026-10-25): only a Draft can be changed or deleted.
  trade: {
    'Draft': 'Being prepared. It can be changed, saved over or deleted. Next: Email to client.',
    'With client': 'Emailed to the client for approval. It can no longer be changed. Next: Mark approved, or Reject.',
    'Approved by client': 'The client approved these orders. Next: Send for execution to the bank\'s trade desk.',
    'Sent for execution': 'Emailed to the bank\'s trade desk. Next: Mark executed.',
    'Executed': 'The bank executed the orders. Final.',
    'Cancelled': 'Ended without execution, with a reason (by the firm, or after the client rejected it). Final.',
    'Revised': 'The client rejected it and it was revised: the changes are in a new trade, with a new number. Final.',
  },
  security: {
    Deleted: 'Removed from the securities list. Kept for the record and the price history.',
  },
  user: {
    Active: 'Can sign in and use the app with this role.',
    Deleted: 'Can no longer sign in. An Admin can restore the user.',
  },
};

/** The tooltip for a status of a kind of record, or undefined when there is none. */
export function statusHelp(entity: StatusHelpEntity, status: string): string | undefined {
  return STATUS_HELP[entity][status];
}
